/**
 * Sets CSS custom properties on an element for:
 * - its largest side (max of width/height),
 * - its width,
 * - its height,
 * keeps them updated on resize, and returns a handle with .destroy().
 *
 * @param {Element} el - The element to observe.
 * @param {string} largestSideProp - CSS custom property name for largest side (default: --largest-side).
 * @param {string} widthProp - CSS custom property name for width (default: --width).
 * @param {string} heightProp - CSS custom property name for height (default: --height).
 * @returns {{ destroy: () => void }}
 */
export function bindElementSizeProperties(
  el,
  largestSideProp = '--largest-side',
  widthProp = '--width',
  heightProp = '--height',
) {
  if (!(el instanceof Element)) {
    throw new TypeError('Expected a DOM Element');
  }

  const applySizes = (w, h) => {
    const width = Math.round(w);
    const height = Math.round(h);
    const largest = Math.max(width, height);

    el.style.setProperty(largestSideProp, `${largest}px`);
    el.style.setProperty(widthProp, `${width}px`);
    el.style.setProperty(heightProp, `${height}px`);
  };

  const getSizeFromEntry = (entry) => {
    let w;
    let h;

    // Prefer border-box (includes padding+border) if available
    if (entry.borderBoxSize) {
      const box = Array.isArray(entry.borderBoxSize)
        ? entry.borderBoxSize[0]
        : entry.borderBoxSize;

      if (box && 'inlineSize' in box && 'blockSize' in box) {
        w = box.inlineSize;
        h = box.blockSize;
      }
    }

    // Fallbacks
    if (w == null || h == null) {
      if (entry.contentRect) {
        w = entry.contentRect.width;
        h = entry.contentRect.height;
      } else {
        const r = entry.target.getBoundingClientRect();
        w = r.width;
        h = r.height;
      }
    }

    return { w, h };
  };

  const updateFromEntry = (entry) => {
    const { w, h } = getSizeFromEntry(entry);
    applySizes(w, h);
  };

  const updateImmediate = () => {
    const r = el.getBoundingClientRect();
    applySizes(r.width, r.height);
  };

  // Initial set
  updateImmediate();

  // Observe with ResizeObserver if available
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) updateFromEntry(entry);
    });
    try {
      ro.observe(el, { box: 'border-box' });
    } catch {
      // Some browsers may not support {box}; fall back
      ro.observe(el);
    }

    return {
      destroy() {
        try {
          ro.unobserve(el);
        } catch {}
        ro.disconnect();

        el.style.removeProperty(largestSideProp);
        el.style.removeProperty(widthProp);
        el.style.removeProperty(heightProp);
      },
    };
  }

  // Fallback when ResizeObserver isn't supported
  const onWinResize = () => updateImmediate();
  window.addEventListener('resize', onWinResize, { passive: true });

  return {
    destroy() {
      window.removeEventListener('resize', onWinResize);

      el.style.removeProperty(largestSideProp);
      el.style.removeProperty(widthProp);
      el.style.removeProperty(heightProp);
    },
  };
}
